import { t } from "../../../lib/sprache";

/**
 * Mahn-Versandfenster (K-410): kleine Regeln ohne React, damit Seite und Tests dieselben nutzen. Verlauf der Mahnungen seit dem
 * letzten Zurücksetzen (wie app.dunning_fee_for, K-320), Frist in Tagen, Seitenbild als Bildquelle.
 */

export interface MahnEintrag {
  id: number;
  stage: string;
  /** Tag des Versands bzw. des Nachtrags */
  on: string | null;
  /** gesetzte Frist dieser Stufe */
  deadline: string | null;
  /** außerhalb des Portals versandt und nachgetragen */
  recorded: boolean;
}

/** Gesendete und nachgetragene Mahnstufen seit dem letzten dunning_reset, älteste zuerst */
export function mahnVerlauf(events: readonly { id: number; event: string; at: string; details: Record<string, unknown> | null }[]): MahnEintrag[] {
  const lastReset = Math.max(0, ...events.filter((e) => e.event === "dunning_reset").map((e) => Number(e.id)));
  return events
    .filter((e) => Number(e.id) > lastReset && (e.event === "dunning_sent" || e.event === "dunning_recorded"))
    .filter((e) => typeof e.details?.["stage"] === "string")
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((e) => {
      const deadline = e.details?.["deadline"];
      return {
        id: Number(e.id),
        stage: String(e.details!["stage"]),
        on: String(e.at ?? "").slice(0, 10) || null,
        deadline: typeof deadline === "string" && /^\d{4}-\d{2}-\d{2}/.test(deadline) ? deadline.slice(0, 10) : null,
        recorded: e.event === "dunning_recorded",
      };
    });
}

/**
 * Steuerprüfung L14: Hinweis im Mahnfenster und an der Rechnung, wenn keine Zahlungsfrist vertraglich vereinbart ist. Die Frist auf
 * der Rechnung allein begründet keinen Verzug (§ 286 BGB); ersteVerzugsmahnung: diese Mahnung begründet ihn erst.
 */
export function verzugOhneFristHinweis(o: { agreed: boolean; ersteVerzugsmahnung?: boolean }): string | null {
  if (o.agreed) return null;
  return t(
    `Keine vertraglich vereinbarte Zahlungsfrist gefunden (unterschriebener Vertrag mit Zahlungsklausel zur Kooperation). Die Frist auf der Rechnung allein begründet keinen Verzug: Verzugszinsen und Pauschale laufen erst ab der ersten Mahnung nach Fälligkeit oder 30 Tage nach Zugang, je nachdem, was früher liegt (§ 286 BGB).${o.ersteVerzugsmahnung ? " Diese Mahnung begründet den Verzug erst und trägt deshalb weder Pauschale noch Zinsen." : ""}`,
    `No contractually agreed payment term found (signed contract with a payment clause for the deal). The term on the invoice alone does not put the customer in default: default interest and the flat fee only run from the first dunning notice after the due date or 30 days after receipt, whichever is earlier (§ 286 German Civil Code).${o.ersteVerzugsmahnung ? " This notice only puts the customer in default and therefore carries neither flat fee nor interest." : ""}`);
}

/** Ganze Tage von a bis b (ISO-Daten) */
export function tageZwischen(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** Tag nach `today` (früheste zulässige neue Frist, K-410.6) */
export function morgen(today: string): string {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/** Hinweis unter der neuen Frist: wie viele Tage die Marke hat; heute oder früher ist nicht zulässig */
export function mahnFristText(today: string, deadline: string): string {
  if (!deadline) return t("Bitte eine Frist wählen.", "Please choose a deadline.");
  const n = tageZwischen(today, deadline);
  if (n <= 0) return t("Die Frist muss nach dem heutigen Tag liegen.", "The deadline must be after today.");
  return n === 1 ? t("1 Tag ab heute", "1 day from today") : t(`${n} Tage ab heute`, `${n} days from today`);
}

/** Seitenbild (SVG-Text) als Bildquelle für <img>: kein Skript, kein eingebetteter PDF-Betrachter */
export function sheetSrc(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * K-427: Ein Pflichtfeld ist ungültig, während die Angaben ausgeblendet sind (Ansicht „Schreiben“ in einer Spalte). Der Browser
 * kann das Feld dann weder zeigen noch fokussieren, und „senden“ tat scheinbar nichts. Diese Regel schaltet auf die Angaben um
 * und bringt das Feld danach in Sicht. Rückgabe: true, wenn umgeschaltet wurde (dann kurze Meldung zeigen).
 */
export function ungueltigesFeldZeigen(
  feld: { focus: (o?: { preventScroll?: boolean }) => void; scrollIntoView: (o?: ScrollIntoViewOptions) => void; reportValidity?: () => boolean },
  angabenSichtbar: boolean,
  umschalten: () => void,
  spaeter: (fn: () => void) => void = (fn) => setTimeout(fn, 60),
): boolean {
  if (angabenSichtbar) return false;
  umschalten();
  spaeter(() => {
    feld.scrollIntoView({ block: "center", behavior: "smooth" });
    feld.focus({ preventScroll: true });
    feld.reportValidity?.();
  });
  return true;
}

/**
 * K-419: Angaben, auf denen ein eigener Text beruht (beim Beginn des Bearbeitens festgehalten, so formatiert wie im Text): neue
 * Frist, Gebühr, Zinsen, Gesamtforderung und Stufe. Ändert sich danach eine davon, nennt der eigene Text womöglich noch die alte.
 */
export interface TextBasis { frist: string; gebuehr: string; zinsen: string; gesamt: string; stufe: string }

const BASIS_NAME: Record<keyof TextBasis, () => string> = {
  frist: () => t("Frist", "deadline"), gebuehr: () => t("Gebühr", "fee"), zinsen: () => t("Zinsen", "interest"),
  gesamt: () => t("Gesamtforderung", "total claim"), stufe: () => t("Stufe", "level"),
};

/**
 * Was sich seit dem Beginn des eigenen Texts geändert hat (`geaendert`: Bezeichnung mit alt → neu) und welche alten Angaben
 * wörtlich noch im Text stehen (`imText`). Leer, wenn alles passt.
 */
export function veralteterText(basis: TextBasis | null, jetzt: TextBasis, text: string): { geaendert: string[]; imText: string[] } {
  if (!basis) return { geaendert: [], imText: [] };
  const geaendert: string[] = [];
  const imText: string[] = [];
  for (const k of Object.keys(BASIS_NAME) as (keyof TextBasis)[]) {
    const alt = basis[k].trim();
    const neu = jetzt[k].trim();
    if (alt === neu) continue;
    geaendert.push(`${BASIS_NAME[k]()} ${alt || "–"} → ${neu || "–"}`);
    if (alt && text.includes(alt)) imText.push(alt);
  }
  return { geaendert, imText: [...new Set(imText)] };
}
